/**
 * Shared utilities for 3D scene components.
 *
 * NOTE on hex colors: the values returned by getCategoryColor are 3D
 * scene category colors — they're consumed as Three.js material colors
 * and as the inline backgroundColor for matching list/legend dots. They
 * are intentionally NOT design-system tokens because they need to be
 * stable across themes (a "chair" should look like the same color
 * whether the user is in light or dark mode) and because the design
 * system doesn't have 11 distinct semantic categories. Audits flagging
 * these as "drifted UI colors" are false positives; this file is
 * 3D-rendering domain, not UI.
 */

/**
 * World-axis colors, shared between the 3D viewer's axis gizmo and the
 * floor-plan editor's nudge controls — the whole point is that the arrow a
 * user presses is the SAME color as the line it moves along. Chosen to stay
 * clear of the scene's other reserved colors: selection red (#ef4444),
 * ghost blue (#3b82f6), the category palette below (incl. the purple-400
 * default and the emerald-400 seating green), and the pending-placement
 * amber overlay.
 */
export const AXIS_COLORS = {
    x: '#f97316', // orange-500 — X row + input (plan east/west)
    y: '#16a34a', // green-600 — Y row + input (elevation, up/down)
    z: '#06b6d4', // cyan-500 — Z row + input (plan north/south)
};

/**
 * Heading accent: the arrow at the walk start (and at the chosen anchor
 * corner) that spins as the run-rotation field changes, plus the rotation
 * control itself. Deliberately NOT an axis color — rotation happens AROUND
 * Y, not along any line — and fuchsia is unused elsewhere in the scene.
 */
export const HEADING_COLOR = '#d946ef'; // fuchsia-500

/**
 * Resize-pin accent: the pillar marking which END of a fixed (scan-
 * imported) wall stays pinned while its width is edited. Distinct from
 * HEADING_COLOR because the anchor-corner markers (fuchsia) can be on
 * screen at the same corners at the same time — the pin must read as a
 * different affordance. Lime is unused by the axis, ghost, selection, and
 * category palettes above.
 */
export const RESIZE_PIN_COLOR = '#84cc16'; // lime-500

/**
 * Returns a color for a given object category.
 * Used for consistent coloring across the 3D viewer and object list.
 *
 * @param {string|object} category - Category name or object like { "storage": {} }
 * @returns {string} Hex color code
 */
export const getCategoryColor = (category) => {
    // category can be string or object like {"storage": {}}
    // normalization
    let key = category;
    if (typeof category === 'object' && category !== null) {
        key = Object.keys(category)[0];
    }
    key = (key || 'unknown').toLowerCase();

    if (key.includes('storage') || key.includes('cabinet')) return '#60a5fa'; // Blue-400
    if (key.includes('table') || key.includes('desk')) return '#fbbf24'; // Amber-400
    if (key.includes('chair') || key.includes('seating') || key.includes('sofa')) return '#34d399'; // Emerald-400
    if (key.includes('bed')) return '#f472b6'; // Pink-400
    if (key.includes('television') || key.includes('screen')) return '#1e293b'; // Slate-800
    if (key.includes('refrigerator')) return '#94a3b8'; // Slate-400
    if (key.includes('stove') || key.includes('oven')) return '#ef4444'; // Red-500
    if (key.includes('dishwasher') || key.includes('washer')) return '#cbd5e1'; // Slate-300
    if (key.includes('door')) return '#78350f'; // Brown
    // Blue-400, shared with storage/cabinet (different list sections, so no
    // ambiguity). Must match getRenderStyle('window') in FloorPlanViewer so
    // the sidebar dot agrees with the 3D mesh — doors follow the same rule.
    if (key.includes('window')) return '#60a5fa';

    return '#a78bfa'; // Purple-400 default
};

/**
 * Get category name from category object or string.
 *
 * @param {string|object} category - Category name or object like { "storage": {} }
 * @returns {string} Normalized category name
 */
export const getCategoryName = (category) => {
    if (typeof category === 'object' && category !== null) {
        return (Object.keys(category)[0] || 'unknown').toLowerCase();
    }
    if (typeof category === 'string' && category) {
        return category.toLowerCase();
    }
    return 'unknown';
};
